import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, shareReplay } from 'rxjs';

@Injectable()
export class KanbanSelectionService<T extends { id: Nullable<number> }> {
  selectedItems$ = new BehaviorSubject<T[]>([]);

  selectedItemsMap$: Observable<Map<Nullable<number>, boolean>> =
    this.selectedItems$.pipe(
      map(items => new Map(items.map(item => [item.id, true]))),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  isSelected(item: T, selectedItems: T[]) {
    return !!selectedItems.find(selectedItem => selectedItem.id === item.id);
  }

  selectItems(items: T | T[]) {
    const itemsArray = Array.isArray(items) ? items : [items];
    const newIds = new Set(itemsArray.map(item => item.id));

    this.selectedItems$.next([
      ...this.selectedItems$.value.filter(item => !newIds.has(item.id)),
      ...itemsArray.filter(
        newItem =>
          !this.selectedItems$.value.some(item => item.id === newItem.id),
      ),
    ]);
  }

  selectAllUntil(lastItem: T, items: T[]) {
    const selectedItems = this.selectedItems$.value;
    const firstSelectedItem = selectedItems[0];
    const start = Math.max(
      items.findIndex(item => item.id === firstSelectedItem?.id),
      0,
    );
    const end = items.findIndex(item => item.id === lastItem.id) + 1;
    const itemsRange = items.slice(start, end);
    this.selectedItems$.next(itemsRange);
  }

  clearSelectedItems() {
    this.selectedItems$.next([]);
  }

  removeItems(items: T | T[]) {
    const arr = Array.isArray(items) ? items : [items];
    const idsToRemove = arr.map(i => i.id);
    const filteredItems = this.selectedItems$.value.filter(
      ({ id }) => !idsToRemove.includes(id),
    );
    this.selectedItems$.next(filteredItems);
  }
}
